import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Bell,
  AlertTriangle,
  Clock,
  Calendar,
  ArrowRight,
  CheckCircle
} from "lucide-react";

function formatCurrency(value) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
}

function formatDate(dateStr) {
  if (!dateStr) return "-";
  return new Date(dateStr + "T00:00:00").toLocaleDateString('pt-BR');
}

export default function AlertasVencimento() {
  const [alertas, setAlertas] = useState(null);
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Fetch count periodically
  useEffect(() => {
    let isMounted = true;
    
    const doFetch = async () => {
      try {
        const response = await axios.get(`${API}/alertas/count`);
        if (isMounted) {
          setCount(response.data.total || 0);
        }
      } catch (error) {
        // Silently ignore errors for background fetches
        if (error.response?.status !== 401) {
          console.error("Erro ao buscar contagem de alertas:", error);
        }
      }
    };
    
    doFetch();
    const interval = setInterval(doFetch, 60000); // Update every minute
    
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, []);

  // Fetch full data when popover opens
  useEffect(() => {
    if (open) {
      fetchAlertas();
    }
  }, [open]);

  const fetchCount = async () => {
    try {
      const response = await axios.get(`${API}/alertas/count`);
      setCount(response.data.total || 0);
    } catch (error) {
      console.error("Erro ao buscar contagem de alertas:", error);
    }
  };

  const fetchAlertas = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/alertas/vencimentos`);
      setAlertas(response.data);
    } catch (error) {
      if (error.response?.status !== 401) {
        console.error("Erro ao buscar alertas:", error);
      }
      setAlertas(null);
    } finally {
      setLoading(false);
    }
  };

  const handleBaixar = async (id) => {
    try {
      await axios.post(`${API}/lancamentos/${id}/baixar`);
      fetchAlertas();
      fetchCount();
    } catch (error) {
      console.error("Erro ao baixar lançamento:", error);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          data-testid="alertas-bell-button"
        >
          <Bell className="h-5 w-5 text-slate-600" />
          {count > 0 && (
            <Badge 
              className="absolute -top-1 -right-1 h-5 min-w-5 px-1 bg-red-500 text-white text-xs flex items-center justify-center"
              data-testid="alertas-badge"
            >
              {count > 99 ? "99+" : count}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-[400px] p-0" 
        align="end"
        data-testid="alertas-popover"
      >
        <div className="p-4 border-b bg-slate-50">
          <h3 className="font-semibold text-slate-800">Alertas de Vencimento</h3>
          <p className="text-sm text-slate-500">Parcelas que requerem atenção</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-luvcorp-navy"></div>
          </div>
        ) : alertas ? (
          <ScrollArea className="max-h-[400px]">
            {/* Vencidos */}
            {alertas.vencidos.quantidade > 0 && (
              <div className="p-3 border-b">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="h-4 w-4 text-red-500" />
                  <span className="font-medium text-red-700">
                    Vencidos ({alertas.vencidos.quantidade})
                  </span>
                  <span className="ml-auto text-sm font-medium text-red-600">
                    {formatCurrency(alertas.vencidos.total)}
                  </span>
                </div>
                <div className="space-y-2">
                  {alertas.vencidos.items.slice(0, 5).map((item) => (
                    <AlertItem 
                      key={item.id} 
                      item={item} 
                      variant="vencido"
                      onBaixar={handleBaixar}
                    />
                  ))}
                  {alertas.vencidos.quantidade > 5 && (
                    <p className="text-xs text-slate-500 text-center">
                      + {alertas.vencidos.quantidade - 5} outros vencidos
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Vencendo Hoje */}
            {alertas.vencendo_hoje.quantidade > 0 && (
              <div className="p-3 border-b">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="h-4 w-4 text-amber-500" />
                  <span className="font-medium text-amber-700">
                    Vence Hoje ({alertas.vencendo_hoje.quantidade})
                  </span>
                  <span className="ml-auto text-sm font-medium text-amber-600">
                    {formatCurrency(alertas.vencendo_hoje.total)}
                  </span>
                </div>
                <div className="space-y-2">
                  {alertas.vencendo_hoje.items.slice(0, 5).map((item) => (
                    <AlertItem 
                      key={item.id} 
                      item={item} 
                      variant="hoje"
                      onBaixar={handleBaixar}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Próximos 7 dias */}
            {alertas.proximos.quantidade > 0 && (
              <div className="p-3 border-b">
                <div className="flex items-center gap-2 mb-2">
                  <Calendar className="h-4 w-4 text-blue-500" />
                  <span className="font-medium text-blue-700">
                    Próximos 7 dias ({alertas.proximos.quantidade})
                  </span>
                  <span className="ml-auto text-sm font-medium text-blue-600">
                    {formatCurrency(alertas.proximos.total)}
                  </span>
                </div>
                <div className="space-y-2">
                  {alertas.proximos.items.slice(0, 5).map((item) => (
                    <AlertItem 
                      key={item.id} 
                      item={item} 
                      variant="proximo"
                      onBaixar={handleBaixar}
                    />
                  ))}
                  {alertas.proximos.quantidade > 5 && (
                    <p className="text-xs text-slate-500 text-center">
                      + {alertas.proximos.quantidade - 5} outros a vencer
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Sem alertas */}
            {alertas.resumo.total_alertas === 0 && (
              <div className="p-8 text-center text-slate-500">
                <CheckCircle className="h-12 w-12 text-green-400 mx-auto mb-2" />
                <p className="font-medium">Tudo em dia!</p>
                <p className="text-sm">Não há parcelas pendentes de atenção</p>
              </div>
            )}
          </ScrollArea>
        ) : null}

        {/* Footer */}
        <div className="p-3 border-t bg-slate-50">
          <Link to="/financeiro/contas" onClick={() => setOpen(false)}>
            <Button variant="ghost" className="w-full justify-between text-sm">
              Ver todas as contas
              <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function AlertItem({ item, variant, onBaixar }) {
  const variantStyles = {
    vencido: "bg-red-50 border-red-200",
    hoje: "bg-amber-50 border-amber-200",
    proximo: "bg-blue-50 border-blue-200"
  };

  return (
    <div className={`p-2 rounded-lg border ${variantStyles[variant]} flex items-center gap-2`}>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-700 truncate">
          {item.parcela && <span className="text-slate-500">{item.parcela} - </span>}
          {item.descricao}
        </p>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <span>{formatDate(item.data_vencimento)}</span>
          {item.cliente_nome && (
            <>
              <span>•</span>
              <span className="truncate">{item.cliente_nome}</span>
            </>
          )}
          {item.dias_atraso && (
            <span className="text-red-600 font-medium">
              ({item.dias_atraso}d atraso)
            </span>
          )}
          {item.dias_para_vencer && (
            <span className="text-blue-600">
              (em {item.dias_para_vencer}d)
            </span>
          )}
        </div>
      </div>
      <div className="text-right">
        <p className={`text-sm font-semibold ${
          item.tipo === "receita" ? "text-green-600" : "text-red-600"
        }`}>
          {formatCurrency(item.valor)}
        </p>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 px-2 text-xs text-green-600 hover:text-green-700 hover:bg-green-100"
          onClick={(e) => {
            e.stopPropagation();
            onBaixar(item.id);
          }}
        >
          <CheckCircle className="h-3 w-3 mr-1" />
          Baixar
        </Button>
      </div>
    </div>
  );
}
